import { useState } from 'react';

export interface ImportacaoRow {
  id: string;
  nomeArquivo: string;
  status: 'processando' | 'revisao' | 'concluida' | 'erro';
  criadaEm: string;
  // Resumo da re-importação (novos/atualizados/…). Null nas importações antigas.
  resultado?: {
    novos?: number;
    atualizados?: number;
    semMudanca?: number;
    ausentes?: number;
    divergencias?: number;
    parcelasPagas?: number;
  } | null;
}

/** "3 novos · 5 atualizados · 2 sem mudança · 1 divergência" a partir do resultado. */
function resumoImportacao(r: ImportacaoRow): string | null {
  const d = r.resultado;
  if (!d) return null;
  const partes = [
    d.novos ? `${d.novos} novo${d.novos > 1 ? 's' : ''}` : null,
    d.atualizados ? `${d.atualizados} atualizado${d.atualizados > 1 ? 's' : ''}` : null,
    d.semMudanca ? `${d.semMudanca} sem mudança` : null,
    d.divergencias ? `${d.divergencias} divergência${d.divergencias > 1 ? 's' : ''}` : null,
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : null;
}

const STATUS_LABEL: Record<ImportacaoRow['status'], { label: string; color: string }> = {
  processando: { label: 'Processando', color: 'var(--ink-3)' },
  revisao: { label: 'Em revisão', color: 'var(--accent-bright)' },
  concluida: { label: 'Concluída', color: 'var(--accent-bright)' },
  erro: { label: 'Erro', color: 'var(--risk)' },
};

function IconeLixeira() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9.5 7V4.8c0-.44.36-.8.8-.8h3.4c.44 0 .8.36.8.8V7m-8 0 .7 12.1a2 2 0 0 0 2 1.9h5.8a2 2 0 0 0 2-1.9L18.5 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Confirmação IN-PLACE (sem modal) para remover uma linha do histórico. A
 * mensagem muda para importações CONCLUÍDAS: remover o registro nunca apaga
 * nem deixa órfãos os contratos já gravados (não há como fazer cascata seguro
 * sem rastreamento importação→contrato no schema hoje — ver deleteImportacao
 * no backend) — o aviso deixa isso explícito para quem clicar.
 */
function LinhaImportacao({
  row,
  onRemover,
}: {
  row: ImportacaoRow;
  onRemover: (id: string) => Promise<void>;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const s = STATUS_LABEL[row.status];

  async function confirmarRemocao() {
    setRemovendo(true);
    setErro(null);
    try {
      await onRemover(row.id);
    } catch {
      setErro('Não foi possível remover agora. Tente de novo.');
      setRemovendo(false);
    }
  }

  if (confirmando) {
    return (
      <li className="flex flex-col gap-2 px-5 py-3" data-testid="importacao-linha-confirmar">
        <div className="text-sm text-ink">
          {row.status === 'concluida'
            ? 'Isso remove o registro desta importação. Os contratos já importados continuam na sua carteira.'
            : 'Remover esta importação?'}
        </div>
        {erro && <div className="text-xs" style={{ color: 'var(--risk)' }}>{erro}</div>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={confirmarRemocao}
            disabled={removendo}
            className="rounded-[8px] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            style={{ background: 'var(--risk)' }}
            data-testid="importacao-confirmar-remocao"
          >
            {removendo ? 'Removendo…' : 'Remover'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmando(false)}
            disabled={removendo}
            className="rounded-[8px] border border-line px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-bg-warm disabled:opacity-60"
          >
            Cancelar
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3" data-testid="importacao-linha">
      <div className="min-w-0">
        <div className="truncate text-sm text-ink">{row.nomeArquivo}</div>
        <div className="num mt-0.5 text-xs text-ink-4">{new Date(row.criadaEm).toLocaleString('pt-BR')}</div>
        {resumoImportacao(row) && (
          <div className="mt-0.5 text-xs text-ink-3" data-testid="importacao-resumo">
            {resumoImportacao(row)}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="text-xs font-medium" style={{ color: s.color }}>
          {s.label}
        </span>
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          title="Remover importação"
          aria-label="Remover importação"
          className="flex h-7 w-7 items-center justify-center rounded-[8px] text-ink-4 transition-colors hover:bg-bg-warm hover:text-risk"
          data-testid="importacao-remover"
        >
          <IconeLixeira />
        </button>
      </div>
    </li>
  );
}

export function HistoryList({
  rows,
  onNova,
  onRemover,
}: {
  rows: ImportacaoRow[];
  onNova: () => void;
  onRemover: (id: string) => Promise<void>;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <span className="text-sm font-medium text-ink-2">Importações</span>
        <button className="text-sm text-accent-bright hover:underline" onClick={onNova}>
          + Nova
        </button>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((r) => (
          <LinhaImportacao key={r.id} row={r} onRemover={onRemover} />
        ))}
      </ul>
    </div>
  );
}
